import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore'
import {
  ALLOWED_USERS_COLLECTION,
  allowedUserDocId,
  isDistrictEmail,
  isOwner,
} from '@budgetmonkey/shared'
import { db } from '@/lib/firebase'

/**
 * Who may use this system, as data — REQUIREMENTS.md §9.
 *
 * The owner is hardcoded in `firestore.rules`, `storage.rules` and
 * `allowlist.ts`; everyone else is a document here, so adding a colleague is a
 * write rather than a deploy of two rules files. The rules are still the gate:
 * these functions are the panel, not the enforcement.
 */

export interface AllowedUser {
  email: string
  addedAt: string
  addedBy: string
  /** May add and remove other people. Granted by the owner alone. */
  admin: boolean
}

export async function fetchAllowedUsers(): Promise<AllowedUser[]> {
  const snapshot = await getDocs(
    query(collection(db, ALLOWED_USERS_COLLECTION), orderBy('addedAt', 'asc')),
  )

  return snapshot.docs.map((document) => {
    const data = document.data()
    return {
      // The document id is the authority — it is what the rules match on.
      // A stored `email` that disagreed with it would be the one shown while
      // the other one governed access.
      email: document.id,
      addedAt: (data.addedAt as string | undefined) ?? '',
      addedBy: (data.addedBy as string | undefined) ?? '',
      admin: data.admin === true,
    }
  })
}

export class AccessRejected extends Error {}

/**
 * Grant access to a district address.
 *
 * The checks here are duplicates of the rules, kept for a decent error message
 * rather than for safety — a rules rejection arrives as "permission denied"
 * with nothing about which of several conditions failed.
 */
export async function addAllowedUser(
  email: string,
  addedBy: string,
  options: { admin?: boolean } = {},
): Promise<void> {
  const id = allowedUserDocId(email)

  if (id === '') throw new AccessRejected('Enter an email address.')
  if (!isDistrictEmail(id)) {
    throw new AccessRejected(
      'Only @southfayette.org addresses can be added. This app holds district spending.',
    )
  }
  if (isOwner(id)) {
    throw new AccessRejected('You already have access — you are the owner.')
  }

  // Re-adding somebody who is already listed used to overwrite their entry
  // with `admin: false`, silently taking the power to manage access away from
  // an administrator — and the rules permit it, because the owner is allowed
  // to write that. Nothing on screen said so. There is nothing to grant here
  // anyway: they already have access.
  const existing = await getDoc(doc(db, ALLOWED_USERS_COLLECTION, id))
  if (existing.exists()) {
    throw new AccessRejected(
      existing.get('admin') === true
        ? 'That address already has access, as an administrator.'
        : 'That address already has access.',
    )
  }

  await setDoc(doc(db, ALLOWED_USERS_COLLECTION, id), {
    email: id,
    addedAt: new Date().toISOString(),
    addedBy: allowedUserDocId(addedBy),
    admin: options.admin === true,
  })
}

/**
 * Make someone an administrator, or stop them being one.
 *
 * The rules refuse this to everyone but the owner, so the UI offers it only to
 * them — and the rules are what makes that true rather than the UI.
 *
 * `addedBy` is rewritten to whoever is making the change, because the rules
 * require the field to match the caller on every write. The record therefore
 * says who last granted this person something, which is the useful reading of
 * it when access is being handed around.
 */
export async function setAllowedUserAdmin(
  user: AllowedUser,
  admin: boolean,
  changedBy: string,
): Promise<void> {
  const id = allowedUserDocId(user.email)
  await setDoc(doc(db, ALLOWED_USERS_COLLECTION, id), {
    email: id,
    addedAt: user.addedAt === '' ? new Date().toISOString() : user.addedAt,
    addedBy: allowedUserDocId(changedBy),
    admin,
  })
}

/**
 * Revoke access.
 *
 * Their data is left exactly where it is. Removing access is not the same
 * decision as destroying a colleague's records, and conflating the two would
 * make this button far more dangerous than it looks.
 */
export async function removeAllowedUser(email: string): Promise<void> {
  await deleteDoc(doc(db, ALLOWED_USERS_COLLECTION, allowedUserDocId(email)))
}
